import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  MenuItem,
} from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const BillingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Current month billing
  const [currentMonthBilling, setCurrentMonthBilling] = useState<any>(null);

  // Consumption (daily breakdown)
  const [consumption, setConsumption] = useState<any>(null);
  const [consumptionDays, setConsumptionDays] = useState(30);

  // Model pricing
  const [modelPricing, setModelPricing] = useState<any[]>([]);

  // Monthly billing query
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyBilling, setMonthlyBilling] = useState<any>(null);

  const getAuthToken = () => {
    return localStorage.getItem('accessToken') || localStorage.getItem('authToken') || localStorage.getItem('token') || '';
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Fetch current month billing on mount
  useEffect(() => {
    fetchCurrentMonthBilling();
    fetchConsumption();
    fetchModelPricing();
  }, []);

  const fetchCurrentMonthBilling = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/billing/current-month', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch billing');
      }

      const data = await response.json();
      setCurrentMonthBilling(data);
      showMessage('success', '✅ Current month billing loaded');
    } catch (error: any) {
      showMessage('error', `❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsumption = async (days: number = 30) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/billing/consumption?days=${days}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch consumption');
      }

      const data = await response.json();
      setConsumption(data);
    } catch (error: any) {
      showMessage('error', `❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyBilling = async (year: number, month: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/billing/monthly?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch monthly billing');
      }

      const data = await response.json();
      setMonthlyBilling(data);
      showMessage('success', `✅ Billing for ${year}-${String(month).padStart(2, '0')} loaded`);
    } catch (error: any) {
      showMessage('error', `❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchModelPricing = async () => {
    try {
      const response = await fetch('/api/billing/pricing/models', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pricing');
      }

      const data = await response.json();
      // Convert string values to numbers
      const models = (data.models || []).map((m: any) => ({
        ...m,
        input_price_per_1k_tokens: Number(m.input_price_per_1k_tokens) || 0,
        output_price_per_1k_tokens: Number(m.output_price_per_1k_tokens) || 0,
        minimum_cost_per_call: Number(m.minimum_cost_per_call) || 0,
      }));
      setModelPricing(models);
    } catch (error: any) {
      showMessage('error', `❌ ${error.message}`);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setMessage(null);
  };

  const handleConsumptionDaysChange = async (days: number) => {
    setConsumptionDays(days);
    await fetchConsumption(days);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        💰 Billing Dashboard
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Billing & Consumption"
          subheader="Track your token usage and costs"
        />
        <CardContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="billing tabs"
            sx={{ mb: 2 }}
          >
            <Tab label="📊 Current Month" id="billing-tab-0" />
            <Tab label="📈 Daily Consumption" id="billing-tab-1" />
            <Tab label="📅 Monthly History" id="billing-tab-2" />
            <Tab label="💲 Model Pricing" id="billing-tab-3" />
          </Tabs>

          {loading && <CircularProgress />}

          {/* Current Month Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box>
              <Button
                variant="contained"
                onClick={fetchCurrentMonthBilling}
                sx={{ mb: 2 }}
                disabled={loading}
              >
                🔄 Refresh
              </Button>

              {currentMonthBilling && (
                <Box>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    {currentMonthBilling.billing_month} - Total: ${currentMonthBilling.total_cost_usd.toFixed(4)}
                  </Typography>

                  {Object.entries(currentMonthBilling.services || {}).map(([serviceName, serviceData]: any) => (
                    <Card key={serviceName} sx={{ mb: 2 }}>
                      <CardHeader
                        title={`${serviceName.toUpperCase()} - $${serviceData.total_cost_usd.toFixed(4)}`}
                        subheader={`${serviceData.operations.length} operation types`}
                      />
                      <CardContent>
                        {serviceData.operations.map((op: any) => (
                          <Box key={op.operation_type} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {op.operation_type}
                            </Typography>
                            <TableContainer component={Paper} sx={{ mb: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell>Model</TableCell>
                                    <TableCell align="right">Tokens</TableCell>
                                    <TableCell align="right">Calls</TableCell>
                                    <TableCell align="right">Cost</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(op.models || {}).map(([modelName, modelData]: any) => (
                                    <TableRow key={modelName}>
                                      <TableCell>{modelName}</TableCell>
                                      <TableCell align="right">
                                        {modelData.total_tokens.toLocaleString()}
                                      </TableCell>
                                      <TableCell align="right">
                                        {modelData.call_count}
                                      </TableCell>
                                      <TableCell align="right">
                                        ${modelData.cost_usd.toFixed(4)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Consumption Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant={consumptionDays === 7 ? 'contained' : 'outlined'}
                  onClick={() => handleConsumptionDaysChange(7)}
                  disabled={loading}
                >
                  7 days
                </Button>
                <Button
                  variant={consumptionDays === 30 ? 'contained' : 'outlined'}
                  onClick={() => handleConsumptionDaysChange(30)}
                  disabled={loading}
                >
                  30 days
                </Button>
                <Button
                  variant={consumptionDays === 90 ? 'contained' : 'outlined'}
                  onClick={() => handleConsumptionDaysChange(90)}
                  disabled={loading}
                >
                  90 days
                </Button>
              </Box>

              {consumption && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Last {consumption.period_days} days - Total: ${consumption.total_cost_usd.toFixed(4)}
                  </Typography>

                  {consumption.daily_breakdown.map((day: any) => (
                    <Card key={day.date} sx={{ mb: 1 }}>
                      <CardHeader
                        title={`${day.date} - $${day.total_cost_usd.toFixed(4)}`}
                        titleTypographyProps={{ variant: 'subtitle2' }}
                        sx={{ py: 1 }}
                      />
                      <CardContent sx={{ py: 1 }}>
                        {Object.entries(day.services || {}).map(([serviceName, serviceData]: any) => (
                          <Box key={serviceName} sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {serviceName}
                            </Typography>
                            {Object.entries(serviceData.operations || {}).map(([opType, opData]: any) => (
                              <Box key={opType} sx={{ ml: 2, mb: 0.5 }}>
                                <Typography variant="caption">
                                  {opType}:{' '}
                                  {Object.entries(opData.models || {})
                                    .map(
                                      ([modelName, modelData]: any) =>
                                        `${modelName}: ${modelData.total_tokens} tokens ($${modelData.cost_usd.toFixed(4)})`,
                                    )
                                    .join(' | ')}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Monthly History Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Year"
                    type="number"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    fullWidth
                    size="small"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {new Date(2026, i, 1).toLocaleString('default', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                onClick={() => fetchMonthlyBilling(selectedYear, selectedMonth)}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                📊 Get Billing
              </Button>

              {monthlyBilling && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {monthlyBilling.billing_month} - Total: ${monthlyBilling.total_cost_usd.toFixed(4)}
                  </Typography>

                  {Object.entries(monthlyBilling.services || {}).map(([serviceName, serviceData]: any) => (
                    <Card key={serviceName} sx={{ mb: 2 }}>
                      <CardHeader
                        title={`${serviceName.toUpperCase()} - $${serviceData.total_cost_usd.toFixed(4)}`}
                      />
                      <CardContent>
                        {serviceData.operations.map((op: any) => (
                          <TableContainer key={op.operation_type} component={Paper} sx={{ mb: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                                    {op.operation_type}
                                  </TableCell>
                                </TableRow>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                  <TableCell>Model</TableCell>
                                  <TableCell align="right">Tokens</TableCell>
                                  <TableCell align="right">Calls</TableCell>
                                  <TableCell align="right">Cost</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(op.models || {}).map(([modelName, modelData]: any) => (
                                  <TableRow key={modelName}>
                                    <TableCell>{modelName}</TableCell>
                                    <TableCell align="right">
                                      {modelData.total_tokens.toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right">
                                      {modelData.call_count}
                                    </TableCell>
                                    <TableCell align="right">
                                      ${modelData.cost_usd.toFixed(4)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Model Pricing Tab */}
          <TabPanel value={tabValue} index={3}>
            <Box>
              <Button
                variant="contained"
                onClick={fetchModelPricing}
                sx={{ mb: 2 }}
                disabled={loading}
              >
                🔄 Refresh Pricing
              </Button>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Provider</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Input (/1K)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Output (/1K)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Min Cost
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Version</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modelPricing.map((pricing) => (
                      <TableRow key={pricing.id}>
                        <TableCell>{pricing.model_name}</TableCell>
                        <TableCell>{pricing.service_name}</TableCell>
                        <TableCell>{pricing.provider}</TableCell>
                        <TableCell align="right">
                          ${Number(pricing.input_price_per_1k_tokens).toFixed(6)}
                        </TableCell>
                        <TableCell align="right">
                          ${Number(pricing.output_price_per_1k_tokens).toFixed(6)}
                        </TableCell>
                        <TableCell align="right">
                          ${Number(pricing.minimum_cost_per_call).toFixed(6)}
                        </TableCell>
                        <TableCell>{pricing.version}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingPage;
